import { IChecklist } from "@/interfaces/CheckLists";
import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState } from "../interfaces/common";
import { onSetChecklistParams } from "../store/checklist.slice";
import { useDeleteCheckListMutation, useGetAllCheckLists } from "./useCheckListMutation";
import useDebounce from "./useDebounce";

export const useCheckListTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { orderBy, searchText, pageSize, pageNumber } = useSelector(
    (state: RootState) => state.checklist
  );
  const [search, setSearch] = useState<string>(searchText || "");
  const [checklistsPagination, setChecklistsPagination] = useState({
    page: pageNumber || 0,
    pageSize: pageSize || 50
  });
  const [checklistsSort, setChecklistsSort] = useState({
    field: orderBy?.field || "lastUpdatedDate",
    sort: orderBy?.sort || "desc"
  });
  const [opentAction, setOpentAction] = useState<any>(null);
  const [selectedActionRow, setSelectedActionRow] = useState<any>(null);
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [openPreviewDialog, setOpenPreviewDialog] = useState<boolean>(false);

  const debouncedSearch = useDebounce(search, 500);

  const onShowCreateChecklistDialog = useCallback(() => {
    navigate(`/settings/checklists/create`);
  }, [navigate]);

  const queryParams = useMemo(() => {
    const params: any = {};
    if (debouncedSearch) {
      params.searchText = debouncedSearch.trim();
    }
    if (checklistsPagination) {
      params.pageSize = checklistsPagination.pageSize;
      params.pageNumber = checklistsPagination.page + 1;
    }
    if (checklistsSort) {
      params.orderBy = `${checklistsSort.field} ${checklistsSort.sort}`;
    }
    return params;
  }, [debouncedSearch, checklistsPagination, checklistsSort]);

  const onChecklistAction = useCallback((e: SyntheticEvent, row: IChecklist) => {
    e.stopPropagation();
    setSelectedActionRow(row);
    setOpentAction(e.currentTarget);
  }, []);

  const onEditSelected = useCallback(
    (row: any) => {
      setIsUpdateMode(true);
      navigate(`/settings/checklists/${row?.id}`);
    },
    [navigate]
  );

  const onDeleteSelected = useCallback(() => {
    setOpentAction(null);
    setSelectedActionRow(null);
    setOpenDeleteConfirmationDialog(false);
  }, [setOpentAction, setSelectedActionRow, setOpenDeleteConfirmationDialog]);

  const onSearchChecklist = useCallback((e: any) => {
    setSearch(e.target.value);
    setChecklistsPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const onChecklistsPaginationModelChange = useCallback((model: any) => {
    setChecklistsPagination((prev) => {
      if (model.pageSize !== prev.pageSize) {
        return { page: 0, pageSize: model.pageSize };
      }
      return model;
    });
  }, []);

  const onChecklistsSortModelChange = useCallback(
    (model: any) => {
      const newField = model[0]?.field;
      const newSort = model[0]?.sort;

      if (newField === checklistsSort.field) {
        const toggledSort = checklistsSort.sort === "asc" ? "desc" : "asc";
        setChecklistsSort({ field: newField, sort: toggledSort });
      } else {
        setChecklistsSort({ field: newField, sort: newSort });
      }
    },
    [checklistsSort.field, checklistsSort.sort]
  );

  const openDeleteDialog = useCallback(() => {
    setOpenDeleteConfirmationDialog((prev) => !prev);
    setOpentAction(null);
  }, [setOpentAction, setOpenDeleteConfirmationDialog]);

  useEffect(() => {
    dispatch(
      onSetChecklistParams({
        searchText: search,
        pageSize: checklistsPagination.pageSize,
        pageNumber: checklistsPagination.page,
        orderBy: { field: checklistsSort.field, sort: checklistsSort.sort }
      })
    );
  }, [dispatch, search, checklistsPagination, checklistsSort]);

  //API Calls
  const { checkLists, isLoading } = useGetAllCheckLists(queryParams);
  const { deleteCheckList, isPending: isDeleteLoading } = useDeleteCheckListMutation(
    selectedActionRow?.id,
    onDeleteSelected
  );

  return {
    checklistsPagination,
    checklistsSort,
    search,
    checkLists,
    isLoading,
    opentAction,
    selectedActionRow,
    openDeleteConfirmationDialog,
    isDeleteLoading,
    isUpdateMode,
    openPreviewDialog,
    checklistId: selectedActionRow?.id,
    setOpenDeleteConfirmationDialog,
    setOpentAction,
    onEditSelected,
    onDeleteSelected,
    onChecklistAction,
    onShowCreateChecklistDialog,
    onSearchChecklist,
    onChecklistsPaginationModelChange,
    onChecklistsSortModelChange,
    deleteCheckList,
    openDeleteDialog,
    setSelectedActionRow,
    setOpenPreviewDialog
  };
};
